import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { api } from "../services/api";
import { useAuth } from "./AuthContext";


const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

export const defaultTheme = {
  _id: null,
  name: "Default",
  colors: {
    primary: "#4F46E5",
    secondary: "#10B981",
    accent: "#F59E0B",
    background: "#ffffff",
    surface: "#ffffff",
    border: "#e5e7eb",
    text: "#1f2937",
  },
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [currentThemeId, setCurrentThemeId] = useState(
    () => localStorage.getItem("themeId") || null
  );
  const [customTheme, setCustomTheme] = useState(null);

  const fetchThemeData = useCallback(async () => {
    try {
      // Always fetch public themes (available for everyone)
      const themesRes = await api.get("/theme");

      if (!themesRes?.success) return;

      const { themes: allThemes, activeTheme } = themesRes;
      setThemes(allThemes || []);

      // Only fetch user's personal preference if logged in
      if (!user) {
        // For unauthenticated users, check localStorage for themeId
        const storedThemeId = localStorage.getItem("themeId");
        const storedTheme = (allThemes || []).find(
          (t) => t._id === storedThemeId && t.isEnabled
        );
        if (storedTheme) {
          setCurrentThemeId(storedTheme._id);
          setCustomTheme(storedTheme);
        } else if (activeTheme && activeTheme.isEnabled) {
          setCurrentThemeId(activeTheme._id);
          setCustomTheme(activeTheme);
          localStorage.setItem("themeId", activeTheme._id);
        } else {
          setCurrentThemeId(null);
          setCustomTheme(null);
          localStorage.removeItem("themeId");
        }
        return;
      }

      // Fetch user's personal preference
      let userTheme = null;
      try {
        const userThemeRes = await api.get("/theme/user/theme");
        userTheme = userThemeRes?.success ? userThemeRes.theme : null;

        // Check if user's theme is disabled
        if (userTheme && !userTheme.isEnabled) {
          console.warn(
            "User's selected theme is disabled, falling back to active theme"
          );
          userTheme = null; // Force fallback to active theme or default

          // Clear user's disabled theme preference in backend
          try {
            await api.put("/theme/user/theme", { themeId: "default" });
          } catch (clearError) {
            console.error(
              "Failed to clear disabled theme preference:",
              clearError
            );
          }
        }
      } catch (authError) {
        // Silent fail - use active theme instead
        console.error("Failed to fetch user theme:", authError);
      }

      // Apply theme priority: user preference > active theme > default
      const selectedTheme = userTheme || activeTheme;

      if (selectedTheme) {
        setCurrentThemeId(selectedTheme._id);
        setCustomTheme(selectedTheme);
        localStorage.setItem("themeId", selectedTheme._id);
      } else {
        // No theme selected - use default CSS
        setCurrentThemeId(null);
        setCustomTheme(null);
        localStorage.removeItem("themeId");
      }
    } catch (error) {
      console.error("Failed to fetch themes:", error);
      setCustomTheme(null);
      setCurrentThemeId(null);
    }
  }, [user]);

  useEffect(() => {
    fetchThemeData();
  }, [fetchThemeData]);

  useEffect(() => {
    const root = document.documentElement;
    if (customTheme) {
      applyTheme(customTheme);
    } else {
      // Clear inline overrides so CSS (including dark mode) drives the palette
      Object.keys(defaultTheme.colors).forEach((key) => {
        root.style.removeProperty(`--color-${key}`);
      });
    }
  }, [customTheme]);

  const applyTheme = (theme) => {
    if (!theme?.colors) return;
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
  };

  const cycleTheme = async () => {
    const palette = [defaultTheme, ...themes];
    const idx = palette.findIndex((t) => t._id === currentThemeId);
    const next = palette[(idx + 1) % palette.length];

    const nextThemeId = next._id || null;
    setCurrentThemeId(nextThemeId);
    setCustomTheme(next._id ? next : null);
    localStorage.setItem("themeId", next._id || "");

    // Persist to backend if user is authenticated
    if (user) {
      try {
        await api.put("/theme/user/theme", {
          themeId: nextThemeId === null ? "default" : nextThemeId,
        });
      } catch (error) {
        console.error("Failed to save theme preference:", error);
      }
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        cycleTheme,
        currentThemeId,
        themes,
        customTheme,
        refetchTheme: fetchThemeData,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
